/**
 * Product reports listing page.
 */
import {
  FileBarChart,
  Database,
  BarChart3,
  TableProperties,
  Calendar,
  Clock,
  Rows3,
  Columns3,
  Filter,
  Activity,
  Sigma,
} from 'lucide-react';
import { ReportsPage } from '@/components/reports/ReportsPage';
import { ConfigBadge } from '@/components/reports/ConfigBadge';
import { PRESET_LABELS, INTERVAL_LABELS } from '@/lib/reports.types';
import { countFilters } from '@/lib/filters';

const iconCls = 'w-3 h-3 shrink-0';

function renderBadges(config: Record<string, unknown>) {
  const rawEvents = config.eventNames ?? config.events ?? '';
  const events: string[] = Array.isArray(rawEvents)
    ? rawEvents
    : typeof rawEvents === 'string' && rawEvents
      ? rawEvents.split(',').filter(Boolean)
      : [];
  const table = ((config.tableName as string) || (config.table as string) || '');
  const chart = ((config.chartType as string) || (config.chart as string) || 'ts');
  const preset = ((config.datePreset as string) || (config.preset as string) || '');
  const startDate = ((config.startDate as string) || (config.start as string) || '');
  const endDate = ((config.endDate as string) || (config.end as string) || '');
  const count = ((config.countMethod as string) || (config.count as string) || 'all');
  const metric = ((config.metricField as string) || (config.metric as string) || '');
  const calc = ((config.calculationMethod as string) || (config.calc as string) || '');
  const row = ((config.rowDimension as string) || (config.row as string) || '');
  const col = ((config.columnBreakdown as string) || (config.col as string) || '');
  const breakdown = ((config.breakdownProperty as string) || (config.breakdown as string) || '');
  const interval = ((config.timeInterval as string) || (config.interval as string) || 'd');
  const formulaId = ((config.formula as string) || '');
  const filterCount = countFilters(config.filters);

  const dateLabel = preset
    ? PRESET_LABELS[preset] || preset.replace(/_/g, ' ')
    : startDate && endDate ? `${startDate} → ${endDate}` : '';

  return (
    <>
      {table && <ConfigBadge icon={<Database className={iconCls} />} text={table.replace(/_intraday$/, '').replace(/_/g, ' ')} color="slate" />}
      <ConfigBadge
        icon={chart === 'pivot' ? <TableProperties className={iconCls} /> : <BarChart3 className={iconCls} />}
        text={chart === 'pivot' ? 'Pivot' : 'Time Series'}
        color="sky"
      />
      {dateLabel && <ConfigBadge icon={<Calendar className={iconCls} />} text={dateLabel} color="amber" />}
      {interval && interval !== 'd' && interval !== 'days' && (
        <ConfigBadge icon={<Clock className={iconCls} />} text={INTERVAL_LABELS[interval] || interval} color="sky" />
      )}
      {count && count !== 'all' && <ConfigBadge icon={<Sigma className={iconCls} />} text="Unique Users" color="violet" />}
      {metric && <ConfigBadge icon={<Sigma className={iconCls} />} text={`${calc === 'avg' ? 'Avg' : 'Sum'}: ${metric}`} color="violet" />}
      {formulaId && <ConfigBadge icon={<Sigma className={iconCls} />} text={`Formula: ${formulaId}`} color="violet" />}
      {events.map((evt) => <ConfigBadge key={evt} icon={<Activity className={iconCls} />} text={evt} color="brand" />)}
      {row && <ConfigBadge icon={<Rows3 className={iconCls} />} text={`Row: ${row}`} color="emerald" />}
      {col && <ConfigBadge icon={<Columns3 className={iconCls} />} text={`Col: ${col}`} color="emerald" />}
      {breakdown && <ConfigBadge icon={<Columns3 className={iconCls} />} text={`By: ${breakdown}`} color="emerald" />}
      {filterCount > 0 && <ConfigBadge icon={<Filter className={iconCls} />} text={`${filterCount} filter${filterCount > 1 ? 's' : ''}`} color="rose" />}
    </>
  );
}

export function ProductReports() {
  return (
    <ReportsPage
      sectionId="product"
      sectionRoute="/product"
      title="Product"
      subtitle="Explore events with time series and pivot tables"
      emptyIcon={FileBarChart}
      emptyDescription="Create your first product report to start analyzing events"
      showFolders
      accentColor="brand"
      cardIcon={<FileBarChart className="w-4 h-4 text-brand-500" />}
      cardIconBgClass="bg-brand-50"
      renderBadges={renderBadges}
    />
  );
}
